import { Compiler } from '@mode-switch/adapt';
import { FlamingoDataRole } from '@mode-switch/adapter-flamingo';
import {
  Access,
  Aggregate,
  Bin,
  DatePart,
  DateTrunc,
  Difference,
  Expr,
  ExprCall,
  PctDifference,
  PctOfTotal,
} from '@mode-switch/express';
import { Attr, Calc, Column, Field, Model, Names, Values, View } from '@mode-switch/model';
import { DomainResult, ExportPlan, QueryResult, RecordsPlan, SelectPlan, SelectResult } from '@mode-switch/query';
import { ExprFrame } from '@mode-vegas/vegas';
import { List, Record } from 'immutable';
import { ReportTypes } from '..';
import { FieldColor, TextFormat } from './charts';
import { RMap } from './common.types';
import { SwitchViewSourceType } from './data-view';

export type ModeRole = FlamingoDataRole;
export type ModeModel = Model<ModeRole>;
export type ModeFields = { formula: string; alias: string; discrete: boolean; frame?: ExprFrame }[];

export type AttrResourceProps = {
  token?: string;
  isLocal?: boolean;
  desc?: string;
  color?: FieldColor;
  format?: TextFormat;
  defaultsAggregate?: ExprCall.Name;
};

export interface ModeView extends View<ModeRole> {
  attrs: ReadonlyArray<ModeAttr>;
  sourceType?: SwitchViewSourceType;
}

export type ModeField = Field<ModeRole>;
export type ModeCalc = Calc<ModeRole> & AttrResourceProps;
export type ModeColumn = Column<ModeRole> & AttrResourceProps;
export type ModeAttr = View.Attr<ModeRole> & AttrResourceProps;
export type SystemField = Values.Field | Names.Field;
export type ModeFormula = Attr.Formula<ModeRole>;
export type ModeExpr = Expr<ModeRole>;
export type ModeRecordsPlan = RecordsPlan<ModeRole>;
export type ModeSelectPlan = SelectPlan<ModeRole>;
export type ModeSelectResult = SelectResult<ModeRole>;
export type ModeExportPlan = ExportPlan<ModeRole>;

export type ModeCompilerResult = Compiler.Result<ModeRole>;
export type ModeCompiledExpr = Expr.Compiled<ModeRole>;
export type CompiledDateTrunc = DateTrunc.Compiled<ModeRole>;
export type CompiledDatePart = DatePart.Compiled<ModeRole>;
export type CompiledAggregate = Aggregate.Compiled<ModeRole>;
export type CompiledBin = Bin.Compiled<ModeRole>;
export type BaseAggregate = Aggregate.Base;
export type CompiledAccess = Access.Compiled<ModeRole>;
export type CompiledAnalyticWithRelTo = PctDifference.Compiled<ModeRole> | Difference.Compiled<ModeRole>;
export type CompiledAnalyticWithTotalUsing = PctOfTotal.Compiled<ModeRole>;
export type CompiledAnalytic =
  | PctDifference.Compiled<ModeRole>
  | Difference.Compiled<ModeRole>
  | PctOfTotal.Compiled<ModeRole>;

export type BaseAnalytic = PctDifference.Base | Difference.Base;

export type ResultColumn = QueryResult.Column<ModeRole>;
export type ResultColumns = ReadonlyArray<QueryResult.Column<ModeRole>>;

export interface DomainHash {
  [alias: string]: DomainResult<ModeRole>;
}

export const enum PlanStatus {
  Init = 'initialized',
  Loading = 'loading',
  LoadingPage = 'paging', // For Flat Tables
  Failed = 'failed',
  Succeeded = 'succeeded',
}

export interface RecordsResult {
  content: ReadonlyArray<any>;
  columns: ResultColumns;
}

export type FlamingoPlanRef = RMap<string>;

export interface PagingResult {
  offset: number;
  total: number;
  hasNext: boolean;
  hasPrev: boolean;
  pageSize: number;
}

export const enum ResultsType {
  Raw = 'raw',
  Filtered = 'filtered',
  WithCalcs = 'withCalcs',
}

export interface DiscreteValueOptionRecord {
  value: ReportTypes.ReportFilterValue;
  label: string | null;
}

const DefaultDiscreteValueOption: DiscreteValueOptionRecord = {
  value: null,
  label: null,
};

export class DiscreteValueOption extends Record<DiscreteValueOptionRecord>(DefaultDiscreteValueOption) {
  constructor(params?: Partial<DiscreteValueOptionRecord>) {
    params ? super(params) : super();
  }

  with(values: Partial<DiscreteValueOptionRecord>): DiscreteValueOption {
    return this.merge(values);
  }
}

export interface DiscreteValueOptionsRecord {
  isLoaded: boolean;
  values: List<DiscreteValueOption>;
}

const DefaultDiscreteValueOptions: DiscreteValueOptionsRecord = {
  isLoaded: false,
  values: List<DiscreteValueOption>(),
};

export class DiscreteValueOptions extends Record<DiscreteValueOptionsRecord>(DefaultDiscreteValueOptions) {
  constructor(params?: Partial<DiscreteValueOptionsRecord>) {
    params ? super(params) : super();
  }

  with(values: Partial<DiscreteValueOptionsRecord>): DiscreteValueOptions {
    return this.merge(values);
  }
}

export interface RangeValueOptionRecord {
  min: ReportTypes.ReportRangeFilterValue | null;
  max: ReportTypes.ReportRangeFilterValue | null;
}

const DefaultRangeValueOption: RangeValueOptionRecord = {
  min: null,
  max: null,
};

export class RangeValueOption extends Record<RangeValueOptionRecord>(DefaultRangeValueOption) {
  constructor(params?: Partial<RangeValueOptionRecord>) {
    params ? super(params) : super();
  }

  with(values: Partial<RangeValueOptionRecord>): RangeValueOption {
    return this.merge(values);
  }
}

export interface RangeOptionsRecord {
  isLoaded: boolean;
  values: RangeValueOption;
}

const DefaultRangeFilterOptions: RangeOptionsRecord = {
  isLoaded: false,
  values: new RangeValueOption(),
};

export class RangeValueOptions extends Record<RangeOptionsRecord>(DefaultRangeFilterOptions) {
  constructor(params?: Partial<RangeOptionsRecord>) {
    params ? super(params) : super();
  }

  with(values: Partial<RangeOptionsRecord>): RangeValueOptions {
    return this.merge(values);
  }
}
